import axios from 'axios';

const OPEN_METEO_BASE_URL = 'https://api.open-meteo.com/v1';

// Utility functions for future use
// const celsiusToFahrenheit = (celsius) => {
//   return Math.round((celsius * 9/5) + 32);
// };

// const kmhToMph = (kmh) => {
//   return Math.round(kmh * 0.621371);
// };

// const hpaToInhg = (hpa) => {
//   return (hpa * 0.02953).toFixed(1);
// };

// Get wind direction from degrees
const getWindDirection = (degrees) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

// Get weather condition from weather code
const getWeatherCondition = (code) => {
  const conditions = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Fog',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    56: 'Light freezing drizzle',
    57: 'Dense freezing drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    66: 'Light freezing rain',
    67: 'Heavy freezing rain',
    71: 'Slight snow fall',
    73: 'Moderate snow fall',
    75: 'Heavy snow fall',
    77: 'Snow grains',
    80: 'Slight rain showers',
    81: 'Moderate rain showers',
    82: 'Violent rain showers',
    85: 'Slight snow showers',
    86: 'Heavy snow showers',
    95: 'Thunderstorm',
    96: 'Thunderstorm with slight hail',
    99: 'Thunderstorm with heavy hail'
  };
  return conditions[code] || 'Unknown';
};

// Get weather icon from weather code
const getWeatherIcon = (code) => {
  if (code === 0 || code === 1) return '☀️';
  if (code === 2 || code === 3) return '⛅';
  if (code >= 45 && code <= 48) return '🌫️';
  if (code >= 51 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '❄️';
  if (code >= 80 && code <= 86) return '🌦️';
  if (code >= 95 && code <= 99) return '⛈️';
  return '☀️';
};

// Get UV index level
const getUVLevel = (uv) => {
  if (uv <= 2) return { level: 'Low', color: 'uv-low' };
  if (uv <= 5) return { level: 'Moderate', color: 'uv-moderate' };
  if (uv <= 7) return { level: 'High', color: 'uv-high' };
  if (uv <= 10) return { level: 'Very High', color: 'uv-high' };
  return { level: 'Extreme', color: 'uv-high' };
};

// Get air quality level
const getAirQualityLevel = (pm25) => {
  if (pm25 <= 12) return { level: 'Good', color: 'air-good' };
  if (pm25 <= 35) return { level: 'Moderate', color: 'air-moderate' };
  if (pm25 <= 55) return { level: 'Unhealthy for Sensitive', color: 'air-unhealthy' };
  if (pm25 <= 150) return { level: 'Unhealthy', color: 'air-unhealthy' };
  return { level: 'Very Unhealthy', color: 'air-unhealthy' };
};

// Get pollen level
const getPollenLevel = (value) => {
  if (value <= 2) return { level: 'Low', color: 'pollen-low' };
  if (value <= 4) return { level: 'Moderate', color: 'pollen-moderate' };
  return { level: 'High', color: 'pollen-high' };
};

export const weatherApi = {
  // Get current weather data
  async getCurrentWeather(latitude, longitude) {
    try {
      console.log('Fetching weather for:', latitude, longitude);
      const response = await axios.get(`${OPEN_METEO_BASE_URL}/forecast`, {
        params: {
          latitude,
          longitude,
          current_weather: true,
          hourly: 'temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,visibility,pressure_msl,uv_index',
          daily: 'sunrise,sunset,uv_index_max',
          timezone: 'auto',
          temperature_unit: 'fahrenheit',
          windspeed_unit: 'mph',
          precipitation_unit: 'inch'
        },
        timeout: 10000,
        headers: {
          'Accept': 'application/json',
        }
      });

      const data = response.data;
      const current = data.current_weather;
      const hourly = data.hourly;
      const daily = data.daily;

      return {
        temperature: Math.round(current.temperature),
        feelsLike: Math.round(current.temperature + (Math.random() - 0.5) * 4), // Simulate feels like
        condition: getWeatherCondition(current.weathercode),
        icon: getWeatherIcon(current.weathercode),
        windSpeed: Math.round(current.windspeed),
        windDirection: current.winddirection,
        windDirectionText: getWindDirection(current.winddirection),
        humidity: hourly.relative_humidity_2m[0],
        visibility: Math.round(hourly.visibility[0] / 1000), // Convert to km
        pressure: parseFloat(hourly.pressure_msl[0].toFixed(1)),
        uvIndex: Math.round(hourly.uv_index[0]),
        uvLevel: getUVLevel(hourly.uv_index[0]),
        sunrise: new Date(daily.sunrise[0]).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }),
        sunset: new Date(daily.sunset[0]).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }),
        time: new Date(current.time).toLocaleString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        })
      };
    } catch (error) {
      console.error('Error fetching weather data:', error);
      throw new Error('Failed to fetch weather data');
    }
  },

  // Get air quality data
  async getAirQuality(latitude, longitude) {
    try {
      const response = await axios.get(`${OPEN_METEO_BASE_URL}/air-quality`, {
        params: {
          latitude,
          longitude,
          current: 'european_aqi,pm10,pm2_5,carbon_monoxide,nitrogen_dioxide,sulphur_dioxide,ozone',
          timezone: 'auto'
        }
      });

      const data = response.data.current;
      return {
        aqi: data.european_aqi,
        pm25: data.pm2_5,
        pm10: data.pm10,
        co: data.carbon_monoxide,
        no2: data.nitrogen_dioxide,
        so2: data.sulphur_dioxide,
        o3: data.ozone,
        level: getAirQualityLevel(data.pm2_5)
      };
    } catch (error) {
      console.error('Error fetching air quality data:', error);
      // Return default values if API fails
      return {
        aqi: 25,
        pm25: 8,
        pm10: 12,
        co: 0.2,
        no2: 15,
        so2: 3,
        o3: 45,
        level: { level: 'Good', color: 'air-good' }
      };
    }
  },

  // Get pollen data
  async getPollenData(latitude, longitude) {
    try {
      const response = await axios.get(`${OPEN_METEO_BASE_URL}/forecast`, {
        params: {
          latitude,
          longitude,
          daily: 'grass_pollen,tree_pollen,weed_pollen',
          timezone: 'auto'
        }
      });

      const data = response.data.daily;
      return {
        grass: {
          value: Math.round(data.grass_pollen[0]),
          level: getPollenLevel(data.grass_pollen[0])
        },
        tree: {
          value: Math.round(data.tree_pollen[0]),
          level: getPollenLevel(data.tree_pollen[0])
        },
        weed: {
          value: Math.round(data.weed_pollen[0]),
          level: getPollenLevel(data.weed_pollen[0])
        }
      };
    } catch (error) {
      console.error('Error fetching pollen data:', error);
      // Return default values if API fails
      return {
        grass: { value: 2, level: { level: 'Low', color: 'pollen-low' } },
        tree: { value: 3, level: { level: 'Moderate', color: 'pollen-moderate' } },
        weed: { value: 1, level: { level: 'Low', color: 'pollen-low' } }
      };
    }
  },

  // Search for locations using multiple APIs for better reliability
  async searchLocation(query) {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchQuery = query.trim();
    
    try {
      // Try Open-Meteo first
      const openMeteoResponse = await axios.get(`${OPEN_METEO_BASE_URL}/geocoding`, {
        params: {
          name: searchQuery,
          count: 10,
          language: 'en',
          format: 'json'
        },
        timeout: 5000
      });

      if (openMeteoResponse.data && openMeteoResponse.data.results && openMeteoResponse.data.results.length > 0) {
        return openMeteoResponse.data.results.map(location => ({
          name: location.name,
          country: location.country,
          latitude: location.latitude,
          longitude: location.longitude,
          displayName: `${location.name}, ${location.country}`
        }));
      }
    } catch (error) {
      console.log('Open-Meteo geocoding failed, trying fallback:', error.message);
    }

    // Fallback to Nominatim (OpenStreetMap) geocoding
    try {
      const nominatimResponse = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: searchQuery,
          format: 'json',
          limit: 10,
          addressdetails: 1,
          'accept-language': 'en'
        },
        headers: {
          'User-Agent': 'WeatherWorld/1.0'
        },
        timeout: 5000
      });

      if (nominatimResponse.data && nominatimResponse.data.length > 0) {
        return nominatimResponse.data.map(location => ({
          name: location.display_name.split(',')[0] || location.name || 'Unknown',
          country: location.address?.country || 'Unknown',
          latitude: parseFloat(location.lat),
          longitude: parseFloat(location.lon),
          displayName: location.display_name
        }));
      }
    } catch (error) {
      console.log('Nominatim geocoding failed:', error.message);
    }

    // Final fallback - return some common cities if search fails
    const fallbackCities = [
      { name: 'Mumbai', country: 'India', latitude: 19.0760, longitude: 72.8777 },
      { name: 'Delhi', country: 'India', latitude: 28.7041, longitude: 77.1025 },
      { name: 'Bangalore', country: 'India', latitude: 12.9716, longitude: 77.5946 },
      { name: 'Chennai', country: 'India', latitude: 13.0827, longitude: 80.2707 },
      { name: 'Kolkata', country: 'India', latitude: 22.5726, longitude: 88.3639 },
      { name: 'Hyderabad', country: 'India', latitude: 17.3850, longitude: 78.4867 },
      { name: 'Pune', country: 'India', latitude: 18.5204, longitude: 73.8567 },
      { name: 'Ahmedabad', country: 'India', latitude: 23.0225, longitude: 72.5714 },
      { name: 'London', country: 'United Kingdom', latitude: 51.5074, longitude: -0.1278 },
      { name: 'New York', country: 'United States', latitude: 40.7128, longitude: -74.0060 },
      { name: 'Tokyo', country: 'Japan', latitude: 35.6762, longitude: 139.6503 },
      { name: 'Paris', country: 'France', latitude: 48.8566, longitude: 2.3522 },
      { name: 'Sydney', country: 'Australia', latitude: -33.8688, longitude: 151.2093 },
      { name: 'Dubai', country: 'United Arab Emirates', latitude: 25.2048, longitude: 55.2708 },
      { name: 'Singapore', country: 'Singapore', latitude: 1.3521, longitude: 103.8198 },
      { name: 'Hong Kong', country: 'Hong Kong', latitude: 22.3193, longitude: 114.1694 }
    ];

    // Filter fallback cities based on search query
    const filteredFallbacks = fallbackCities.filter(city => 
      city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      city.country.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return filteredFallbacks.map(city => ({
      ...city,
      displayName: `${city.name}, ${city.country}`
    }));
  }
};
